import { useEffect, useRef, useState, type ComponentProps, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { AudioLines, CircleDot, Gauge, Monitor, RefreshCw, Search, Settings2, Type, Volume2, X } from "lucide-react";
import type { StudioController } from "../useStudio";
import type { SettingCategory } from "./SettingsPanel";
import type { ToolSection } from "./ToolsPanel";
import { deferredFeature } from "./DeferredFeature";
import { PromptSurface } from "./PromptSurface";
import { ListeningIndicator } from "./ListeningIndicator";
import "./studio-panels.css";
import "./studio-controls.css";

export type ControlSection = SettingCategory | ToolSection;
const sections = [
  { id: "reading", label: "Text & reading", group: "Prompter settings", icon: Type },
  { id: "playback", label: "Playback", group: "Prompter settings", icon: Gauge },
  { id: "output", label: "Talent display", group: "Prompter settings", icon: Monitor },
  { id: "voice", label: "Speech following", group: "Prompter settings", icon: AudioLines },
  { id: "workspace", label: "Workspace", group: "Prompter settings", icon: Settings2 },
  { id: "speech", label: "Speech setup", group: "Studio tools", icon: AudioLines },
  { id: "rehearsal", label: "Rehearsal", group: "Studio tools", icon: Volume2 },
  { id: "record", label: "Recording", group: "Studio tools", icon: CircleDot },
  { id: "recovery", label: "Recovery & updates", group: "Studio tools", icon: RefreshCw },
] as const;
const isSetting = (section: ControlSection): section is SettingCategory =>
  sections.some(item => item.id === section && item.group === "Prompter settings");

// Keep feature failures isolated: a Tools download must not disable settings.
const SettingsPanel = deferredFeature<
  ComponentProps<typeof import("./SettingsPanel").SettingsPanel>
>(() => import("./SettingsPanel").then(m => ({ default: m.SettingsPanel })), "Settings");
const ToolsPanel = deferredFeature<
  ComponentProps<typeof import("./ToolsPanel").ToolsPanel>
>(() => import("./ToolsPanel").then(m => ({ default: m.ToolsPanel })), "Tools");

export function StudioControls({ studio, section, onSectionChange, onClose }: {
  studio: StudioController;
  section: ControlSection;
  onSectionChange: (section: ControlSection) => void;
  onClose: () => void;
}) {
  const [search, setSearch] = useState("");
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [toolsVisited, setToolsVisited] = useState(!isSetting(section));
  const [narrow, setNarrow] = useState(() => matchMedia("(max-width: 800px)").matches);
  const tabs = useRef<Partial<Record<ControlSection, HTMLButtonElement | null>>>({});
  const selectedSection = useRef(section);
  selectedSection.current = section;
  const searchInput = useRef<HTMLInputElement>(null);
  const body = useRef<HTMLDivElement>(null);
  const settingsSection = useRef<SettingCategory>(isSetting(section) ? section : "reading");
  const toolSection = useRef<ToolSection>(isSetting(section) ? "speech" : section);
  if (isSetting(section)) settingsSection.current = section;
  else toolSection.current = section;

  useEffect(() => {
    if (!isSetting(section)) setToolsVisited(true);
    body.current?.scrollTo({ top: 0 });
    tabs.current[section]?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [section]);
  useEffect(() => {
    const media = matchMedia("(max-width: 800px)");
    const update = () => setNarrow(media.matches);
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  useEffect(() => {
    tabs.current[selectedSection.current]?.focus({ preventScroll: true });
    const escape = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented || document.fullscreenElement) return;
      // Tooltip capture handlers dismiss help first. Closing this nonmodal panel
      // must not also trigger the operator's window-level Escape-to-pause action.
      event.preventDefault();
      event.stopPropagation();
      onClose();
    };
    document.addEventListener("keydown", escape);
    return () => document.removeEventListener("keydown", escape);
  }, [onClose]);

  const choose = (next: ControlSection) => {
    setSearch("");
    setSearchExpanded(false);
    onSectionChange(next);
    body.current?.scrollTo({ top: 0 });
  };
  const navigateTabs = (event: ReactKeyboardEvent<HTMLButtonElement>, current: ControlSection) => {
    const index = sections.findIndex(item => item.id === current);
    let next: number;
    switch (event.key) {
      case "ArrowDown": next = (index + 1) % sections.length; break;
      case "ArrowUp": next = (index - 1 + sections.length) % sections.length; break;
      case "Home": next = 0; break;
      case "End": next = sections.length - 1; break;
      default: return;
    }
    // Navigation here must not trigger the operator's seek or pace shortcuts.
    event.preventDefault();
    event.stopPropagation();
    choose(sections[next].id);
    tabs.current[sections[next].id]?.focus();
  };
  return (
    <aside className="utility-panel studio-controls-drawer" id="studio-controls-panel" aria-label="Settings and tools">
      <div className="utility-heading">
        <h2>Studio controls</h2>
        <button type="button" className="icon-button" aria-label="Close side panel"
          data-help="Close settings and tools and return to the operator desk." onClick={onClose}>
          <X size={20} aria-hidden="true" />
        </button>
      </div>
      <section className="studio-panel studio-controls">
        {narrow && (
          <section className="controls-preview" aria-label="Live talent preview">
            <div className="preview-title"><h3>Talent view</h3><ListeningIndicator studio={studio} /></div>
            <div className="controls-live-preview">
              <PromptSurface state={studio.state} parsed={studio.parsed}
                onSeek={wordIndex => studio.command({ type: "seek", wordIndex })} />
            </div>
          </section>
        )}
        <div className="controls-layout">
          <div className="controls-navigation" role="tablist" aria-label="Studio control sections" aria-orientation="vertical">
            {sections.map((item, index) => {
              const Icon = item.icon;
              return <button key={item.id} ref={element => { tabs.current[item.id] = element; }}
                type="button" role="tab" id={`controls-tab-${item.id}`}
                aria-controls="controls-active-panel" aria-selected={section === item.id}
                tabIndex={section === item.id ? 0 : -1}
                className={index === 5 ? "controls-tab controls-tab-group-start" : "controls-tab"}
                data-help={`Open ${item.label.toLowerCase()} controls.`} data-help-placement="right"
                onClick={() => choose(item.id)} onKeyDown={event => navigateTabs(event, item.id)}>
                <Icon size={17} aria-hidden="true" /><span>{item.label}</span>
              </button>;
            })}
          </div>
          <div className="controls-section">
            {isSetting(section) && (
              <div className={`controls-search-bar ${searchExpanded ? "search-expanded" : ""}`}>
                <button type="button" className="icon-button controls-search-toggle"
                  aria-label={searchExpanded ? "Hide settings search" : "Show settings search"}
                  aria-expanded={searchExpanded} aria-controls="controls-settings-search"
                  data-help="Show or hide settings search in this short window."
                  onClick={() => {
                    setSearchExpanded(value => !value);
                    if (!searchExpanded) requestAnimationFrame(() => searchInput.current?.focus());
                  }}>
                  <Search size={16} aria-hidden="true" />
                </button>
                <div className="panel-search" id="controls-settings-search">
                  <Search size={16} aria-hidden="true" />
                  <input ref={searchInput} type="search" aria-label="Find a setting" placeholder="Find a setting…" value={search}
                    onChange={event => { setSearch(event.target.value); body.current?.scrollTo({ top: 0 }); }} />
                  {search && <button type="button" className="icon-button" aria-label="Clear settings search"
                    data-help="Clear the search and return to the selected settings section." onClick={() => setSearch("")}>
                    <X size={16} aria-hidden="true" />
                  </button>}
                </div>
              </div>
            )}
            <div className="studio-panel-body" ref={body} role="tabpanel" id="controls-active-panel"
              aria-labelledby={`controls-tab-${section}`} tabIndex={0}>
              <div className="controls-content" hidden={!isSetting(section)}>
                <SettingsPanel settings={studio.state.settings} update={studio.updateSettings}
                  selectScrollMode={studio.selectScrollMode} category={settingsSection.current}
                  search={search} clearSearch={() => setSearch("")} />
              </div>
              {(toolsVisited || !isSetting(section)) && (
                <div className="controls-content" hidden={isSetting(section)}>
                  <ToolsPanel studio={studio} tab={toolSection.current} />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </aside>
  );
}
